import { useCallback, useEffect, useRef } from 'react'

import {
    POLLING_INTERVAL_ACTIVE,
    POLLING_INTERVAL_HIDDEN,
    POLLING_INTERVAL_SAVE_DATA,
    POLLING_INTERVAL_2G,
    POLLING_JITTER_MAX,
} from '@/api/actions/notifications/notificationConstants'

export interface UseVisibilityAwareIntervalOptions {
    activeMs?: number
    hiddenMs?: number
    enabled?: boolean
}

/**
 * Hook for visibility-aware polling intervals
 *
 * Automatically adjusts polling frequency based on:
 * - Document visibility (tab active/hidden)
 * - Window focus state
 * - Network connectivity
 * - Speed multiplier for traffic control
 */
export function useVisibilityAwareInterval(callback: () => void, options: UseVisibilityAwareIntervalOptions = {}) {
    const { activeMs = POLLING_INTERVAL_ACTIVE, hiddenMs = POLLING_INTERVAL_HIDDEN, enabled = true } = options
    const intervalRef = useRef<number | null>(null)
    const callbackRef = useRef(callback)
    const pendingRef = useRef(false)

    // Keep callback reference current
    useEffect(() => {
        callbackRef.current = callback
    }, [callback])

    // Get current interval with speed multiplier and network awareness
    const getCurrentInterval = useCallback(() => {
        const base = document.visibilityState === 'visible' ? activeMs : hiddenMs
        let interval = Math.max(0, Math.floor(base * (Number(import.meta.env.VITE_POLLING_SPEED_MULTIPLIER) || 1)))

        // Network-aware pacing
        const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } })
            ?.connection
        if (conn?.saveData) interval = Math.max(interval, POLLING_INTERVAL_SAVE_DATA)
        if (conn?.effectiveType?.includes('2g')) interval = Math.max(interval, POLLING_INTERVAL_2G)

        return interval
    }, [activeMs, hiddenMs])

    // Schedule next interval
    const scheduleNext = useCallback(() => {
        if (intervalRef.current) {
            window.clearTimeout(intervalRef.current)
        }

        if (!enabled) return

        const interval = getCurrentInterval()
        // Add small jitter to prevent thundering herd
        const jitter = Math.floor(Math.random() * POLLING_JITTER_MAX)

        intervalRef.current = window.setTimeout(async () => {
            if (pendingRef.current) return
            pendingRef.current = true
            try {
                await Promise.resolve(callbackRef.current())
            } finally {
                pendingRef.current = false
                scheduleNext()
            }
        }, interval + jitter)
    }, [enabled, getCurrentInterval])

    // Execute callback with debouncing protection
    const run = useCallback(async () => {
        if (pendingRef.current) return
        pendingRef.current = true
        try {
            await Promise.resolve(callbackRef.current())
        } finally {
            pendingRef.current = false
            scheduleNext()
        }
    }, [scheduleNext])

    // Handle visibility and focus changes
    useEffect(() => {
        if (!enabled) return

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                run()
            } else {
                scheduleNext()
            }
        }

        // Prevents extra API calls on focus
        const handleFocus = scheduleNext
        // Prevents extra API calls when reconnecting
        const handleOnline = scheduleNext

        // Set up event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleFocus)
        window.addEventListener('online', handleOnline)

        // Start initial polling
        run()

        return () => {
            // Cleanup
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('online', handleOnline)

            if (intervalRef.current) {
                window.clearTimeout(intervalRef.current)
            }
        }
    }, [enabled, activeMs, hiddenMs, run, scheduleNext])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                window.clearTimeout(intervalRef.current)
            }
        }
    }, [])
}
